/**
 * 邀请码与设备凭证的纯逻辑。
 *
 * 放在 shared 而不是各写一遍，是因为这三处必须**字面一致**：
 *   · 前端输入框边打字边规范化
 *   · 后端收到请求时再规范化一次（永远不要信客户端）
 *   · 切换器和设置页的展示格式
 * 任何一处对"6 位"的理解跑偏（比如允许空格、允许全角数字），
 * 用户就会遇到"我明明输对了却说无效"这种最难查的 bug。
 */

import { INVITE_CODE_LENGTH } from './contracts';

/** 设备凭证的请求头名。前后端共用，改名只改这里。 */
export const HOUSEHOLD_KEY_HEADER = 'x-household-key';

/** 设备凭证长度（十六进制字符数）。256 位熵，不可枚举。 */
export const HOUSEHOLD_KEY_LENGTH = 32;

/**
 * 把用户可能输入的各种形态收敛成 6 位数字。
 *
 * 要处理的实际输入：`123 456`、`123-456`、全角 `１２３４５６`、
 * 从微信复制的 `邀请码：123456`、带尾随换行的粘贴。
 * 做法很简单：**只留数字**，然后取前 6 位。
 */
export function normalizeInviteCode(raw: string): string {
  // 全角数字先转半角，否则会被下面的 \D 直接吃掉
  const halfWidth = raw.replace(/[０-９]/g, (char) =>
    String.fromCharCode(char.charCodeAt(0) - 0xfee0),
  );
  return halfWidth.replace(/\D/g, '').slice(0, INVITE_CODE_LENGTH);
}

export function isCompleteInviteCode(code: string): boolean {
  return /^\d{6}$/.test(code);
}

/** 展示用：`123456` → `123 456`。分组之后一眼能看出有没有少一位。 */
export function formatInviteCode(code: string): string {
  const digits = normalizeInviteCode(code);
  if (digits.length <= 3) return digits;
  return `${digits.slice(0, 3)} ${digits.slice(3)}`;
}

/**
 * 邀请码是否还能用。
 *
 * 注意这里同时判"用过没"和"过期没"：用户的原话是
 * "一次性 + 24 小时兜底"，两条都要，少一条就有一个洞。
 */
export function isInviteUsable(
  invite: { expiresAt: string; usedAt?: string | null },
  now: Date = new Date(),
): boolean {
  if (invite.usedAt) return false;
  const expires = Date.parse(invite.expiresAt);
  if (!Number.isFinite(expires)) return false;
  return expires > now.getTime();
}
